import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Lock, ArrowRight, AlertCircle, Sparkles, Users } from "lucide-react";

export default function CustomerLots() {
  const { user } = useAuth();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lots/available").then((r) => { setLots(r.data); setLoading(false); });
  }, []);

  const kycOk = user?.kyc_status === "approved";

  const tints = [
    "linear-gradient(135deg, rgba(215,25,33,0.85) 0%, rgba(122,16,16,0.85) 100%)",
    "linear-gradient(135deg, rgba(63,107,63,0.85) 0%, rgba(42,74,42,0.85) 100%)",
    "linear-gradient(135deg, rgba(184,134,42,0.85) 0%, rgba(142,101,23,0.88) 100%)",
    "linear-gradient(135deg, rgba(31,24,22,0.7) 0%, rgba(122,16,16,0.85) 100%)",
  ];

  return (
    <div>
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl mb-8 p-8 md:p-10 text-white"
        style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 60%, #7A1010 100%)" }}>
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F2C97A]/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-[#3F6B3F]/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs font-bold uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3 text-[#F2C97A]" /> Available now
          </div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight">Browse curated lots</h1>
          <p className="text-white/80 mt-2 max-w-xl">Each lot is a hand-picked group of healthy young livestock, ready to grow & earn.</p>
        </div>
      </div>

      {!kycOk && (
        <div className="rounded-2xl p-5 mb-6 flex items-center gap-4 border"
          style={{ background: "linear-gradient(90deg, #FFF5DA 0%, #FCEDED 100%)", borderColor: "#F2C97A" }}>
          <Lock className="w-5 h-5 text-[#7A1010]" />
          <div className="flex-1 text-sm text-[#7A1010]">
            Your KYC must be approved before purchasing. <Link to="/kyc" className="underline font-semibold">Complete now</Link>
          </div>
        </div>
      )}

      {loading && <div className="text-center text-[#6B5F58] py-12">Loading lots…</div>}

      {!loading && lots.length === 0 && (
        <div className="gf-card p-10 text-center">
          <AlertCircle className="w-10 h-10 text-[#6B5F58] mx-auto mb-3" />
          <div className="font-display text-xl">No lots available right now</div>
          <p className="text-[#6B5F58] text-sm mt-1">Check back soon — new lots are added regularly.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {lots.map((l, i) => (
          <Link key={l.id} to={`/lots/${l.id}`} data-testid={`lot-card-${l.lot_code}`}
            className={`group rounded-2xl overflow-hidden border border-[#E8E0D2] bg-white hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#D71921]/15 transition-all animate-in delay-${(i % 4) + 1}`}>
            <div className="relative h-44 overflow-hidden">
              <img alt={l.name}
                src={i % 2 ? "https://images.pexels.com/photos/2681870/pexels-photo-2681870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  : "https://images.pexels.com/photos/36744654/pexels-photo-36744654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: tints[i % tints.length], opacity: 0.55 }} />
              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-[#7A1010]">
                {l.lot_code}
              </div>
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <div className="font-display text-2xl drop-shadow-md">{l.name}</div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#6B5F58] line-clamp-2 min-h-[2.5rem]">{l.description}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E8E0D2]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#6B5F58]">Lot price</div>
                  <div className="font-display text-2xl text-[#D71921]">₹{l.price_per_lot.toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FCEDED] text-[#D71921] text-xs font-semibold">
                  <Users className="w-3 h-3" /> {l.animal_count}
                </div>
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#3F6B3F] font-semibold">
                View details <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
